import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Box, xcss } from '@atlaskit/primitives';

import SearchIcon from '@atlaskit/icon/glyph/search';
import Textfield from '@atlaskit/textfield';
import { useAppUpdatesContext } from '@app/app/src/AppContext';
import { useUrlFilterSync } from '../hooks';

export const messages = defineMessages({
  label: {
    id: 'appUpdates.search.label',
    defaultMessage: 'Search release notes',
    description: 'Accessibility label for search input',
  },
  placeholder: {
    id: 'appUpdates.search.placeholder',
    defaultMessage: 'Search release notes',
    description: 'Placeholder text for search input',
  },
});

const searchContainerStyles = xcss({
  minWidth: '200px',
  maxWidth: '400px',
  width: '100%',
});

const searchIconStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  verticalAlign: 'middle',
  paddingLeft: 'space.100',
});

/**
 * A search input component that updates draft filter state.
 */
export const SearchInput: React.FC = () => {
  const { formatMessage } = useIntl();
  const { state, dispatch } = useAppUpdatesContext();
  const { syncFiltersToUrl } = useUrlFilterSync();

  const [localValue, setLocalValue] = React.useState(
    state.updates.draftFilters.searchQuery || '',
  );

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setLocalValue(value);

      dispatch({
        type: 'FILTER_SEARCH_SELECT',
        payload: { searchQuery: value || null },
      });
    },
    [dispatch],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        syncFiltersToUrl({
          ...state.updates.draftFilters,
          cursor: undefined,
        });
        dispatch({
          type: 'UPDATES_FILTERS_APPLY',
        });
      } else if (event.key === 'Escape') {
        setLocalValue('');
        dispatch({
          type: 'FILTER_SEARCH_SELECT',
          payload: { searchQuery: null },
        });
      }
    },
    [dispatch, state.updates.draftFilters, syncFiltersToUrl],
  );

  React.useEffect(() => {
    if (state.updates.draftFilters.searchQuery !== localValue) {
      setLocalValue(state.updates.draftFilters.searchQuery || '');
    }
  }, [state.updates.draftFilters.searchQuery, localValue]);

  return (
    <Box xcss={searchContainerStyles}>
      <Textfield
        testId="app-updates-search-input"
        aria-label={formatMessage(messages.label)}
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={formatMessage(messages.placeholder)}
        elemBeforeInput={
          <Box xcss={searchIconStyles}>
            <SearchIcon label="" size="small" />
          </Box>
        }
        autoComplete="off"
        type="text"
        isCompact={true}
      />
    </Box>
  );
};
